import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { HiveComponent } from '../HiveComponent/HiveComponent';
import './HoneycombTasks.scss';
import { HiveComponentPossibleSizes, HiveComponentPossibleStatuses } from '../HiveComponent/HiveComponent';
/**
 * Converts grid coordinates (columnPosition, rowPosition) to pixel coordinates for a hexagonal layout.
 *
 * Coordinate system:
 *   - Positive columnPosition moves the element right, negative moves left.
 *   - Positive rowPosition moves the element down, negative moves up.
 *
 * @param {object} params - Function parameters
 * @param {number} params.columnPosition - The column position (right/left)
 * @param {number} params.rowPosition - The row position (down/up)
 * @param {number} params.size - Size of the hexagon (flat to flat distance)
 * @returns {object} The pixel coordinates {x, y}
 */
const gridToPixel = ({ columnPosition, rowPosition, size }) => {
  const x = size * (1.25 * columnPosition);
  const y = size * (1.5 * (rowPosition + columnPosition/2));
  return { x, y };
};

/**
 * HoneycombTasks renders a honeycomb grid of services using grid coordinates
 * Each hexagon shares its sides with adjacent hexagons in a proper honeycomb pattern
 *
 * @param {object} params - Component props
 * @param {Array} params.services - Array of service objects to display
 * @param {string} params.defaultSize - Default HiveComponent size (small, medium, large)
 */
export const HoneycombTasks = ({
  services = [],
  defaultSize = HiveComponentPossibleSizes.large,
}) => {
  const containerRef = useRef(null);
  const [spacing, setSpacing] = useState({
    spacesBetween: defaultSize === HiveComponentPossibleSizes.small ? 40 : defaultSize === HiveComponentPossibleSizes.medium ? 80 : 105,
    offsetX: 70,
    offsetY: 190,
  });

  // Read CSS custom properties for responsive spacing
  useEffect(() => {
    const updateSpacing = () => {
      if (containerRef.current) {
        const styles = getComputedStyle(containerRef.current);
        const spacingKey = defaultSize === HiveComponentPossibleSizes.small 
          ? 'spacing-small' 
          : defaultSize === HiveComponentPossibleSizes.medium 
            ? 'spacing-medium' 
            : 'spacing-large';
        
        const spacesBetween = parseFloat(styles.getPropertyValue(`--${spacingKey}`)) || 
          (defaultSize === HiveComponentPossibleSizes.small ? 40 : defaultSize === HiveComponentPossibleSizes.medium ? 80 : 105);
        const offsetX = parseFloat(styles.getPropertyValue('--offset-x')) || 70;
        const offsetY = parseFloat(styles.getPropertyValue('--offset-y')) || 190;
        
        setSpacing({ spacesBetween, offsetX, offsetY });
      }
    };

    updateSpacing();
    window.addEventListener('resize', updateSpacing);
    
    return () => window.removeEventListener('resize', updateSpacing);
  }, [defaultSize]);

  const { spacesBetween, offsetX, offsetY } = spacing;

  return (
    <div 
      ref={containerRef}
      className="honeycomb-tasks-container" 
    >
        {services.map((service, index) => {
          const { x, y } = gridToPixel({
            columnPosition: service.columnPosition,
            rowPosition: service.rowPosition,
            size: spacesBetween
          });
          
          // Calculate position relative to container center
          const posX = x + offsetX;
          const posY = y + offsetY;
          
          return (
            <div
              key={service.id || index}
              className={`honeycomb-cell`}
              style={{
                transform: `translate(${posX}px, ${posY}px)`,
                position: 'absolute',
              }}
              data-column={service.columnPosition}
              data-row={service.rowPosition}
            >
              <HiveComponent
                icon={service.icon}
                secondaryIcon={service.secondaryIcon}
                text={service.text}
                status={service.status || HiveComponentPossibleStatuses.locked}
                size={service.size || defaultSize}
                isInteractive={service.isInteractive !== undefined ? service.isInteractive : true}
                onClick={service.onClick}
              />
            </div>
          );
        })}
      </div>
  );
};

HoneycombTasks.propTypes = {
  /** Array of service objects to display in the honeycomb */
  services: PropTypes.arrayOf(
    PropTypes.shape({
      /** Unique identifier for the service */
      id: PropTypes.string,
      /** Column position in the grid (right/left) */
      columnPosition: PropTypes.number.isRequired,
      /** Row position in the grid (down/up) */
      rowPosition: PropTypes.number.isRequired,
      /** Icon element to display (React element) */
      icon: PropTypes.element,
      /** Secondary icon element to display (React element) */
      secondaryIcon: PropTypes.element,
      /** Text to display in the hexagon (can be string or React node for line breaks) */
      text: PropTypes.node,
      /** Size of the hexagon (small, medium, large) */
      size: PropTypes.oneOf(['small', 'medium', 'large']),
      /** Status of the hexagon (active, completed, locked) */
      status: PropTypes.oneOf(['active', 'completed', 'locked']),
      /** Whether the hexagon is interactive */
      isInteractive: PropTypes.bool,
      /** Click handler function */
      onClick: PropTypes.func,
    })
  ),
  /** Default size for HiveComponent (small, medium, large) */
  defaultSize: PropTypes.oneOf(['small', 'medium', 'large']),
};

export default HoneycombTasks; 